import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';
import type { NewsItem, NewsSection } from '@/lib/types';

/**
 * 뉴스 — 섹션 탭과 기사 목록.
 *
 * 수집은 agent 가 하고 저장은 backend 가 한다. 화면은 <b>읽기만</b> 한다 — 기사를 만드는
 * 엔드포인트가 없다(사람이 뉴스를 손으로 등록하는 곳은 건설동향이지 여기가 아니다).
 *
 * 목 자료로 물러서지 않는다. 뉴스는 "지금 무엇이 올라와 있나" 가 전부라, 가짜 목록을 보여 주면
 * 수집이 멈춘 것을 아무도 눈치채지 못한다 — 빈 화면이 그 사실을 말해 주는 편이 낫다.
 */

const BASE = '/v1/news-headlines';

/** 섹션은 관리자가 어쩌다 한 번 바꾼다. 탭을 오갈 때마다 다시 부르지 않는다. */
const SECTION_STALE_MS = 10 * 60 * 1000;

/** 수집 주기(기본 1시간)보다 짧게 잡아, 화면을 다시 열면 새 기사가 보이게 한다. */
const ITEM_STALE_MS = 5 * 60 * 1000;

export function useNewsSections(): UseQueryResult<NewsSection[]> {
  return useQuery({
    queryKey: queryKeys.newsSections,
    queryFn: () => apiFetch<NewsSection[]>(`${BASE}/sections`),
    staleTime: SECTION_STALE_MS,
  });
}

/**
 * @param sectionId 없으면 전 섹션을 섞어 준다(홈 위젯이 그렇게 읽는다).
 *                  섹션 탭이 아직 안 정해졌으면 `enabled` 로 요청 자체를 미룬다.
 */
export function useNewsItems(sectionId: string | null, limit?: number): UseQueryResult<NewsItem[]> {
  const query = new URLSearchParams();
  if (sectionId) query.set('sectionId', sectionId);
  if (limit) query.set('limit', String(limit));

  return useQuery({
    queryKey: [...queryKeys.newsItems, sectionId ?? 'all', limit ?? 'default'],
    queryFn: () => apiFetch<NewsItem[]>(`${BASE}/items?${query.toString()}`),
    staleTime: ITEM_STALE_MS,
  });
}

/**
 * 기사 하나를 지운다 — <b>부적절한 것을 치우는 유일한 동작</b>이다. 수정도 숨김도 없다.
 *
 * 서버는 지우면서 그 주소를 차단 목록에 남긴다. 남기지 않으면 다음 수집 회차(기본 1시간)가 같은
 * 기사를 새 것으로 보고 다시 넣고, 관리자는 매시간 같은 기사를 지우게 된다.
 */
export function useDeleteNewsItem(): UseMutationResult<unknown, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiFetch<unknown>(`${BASE}/items/${id}`, { method: 'DELETE' }),
    // 섹션별·홈 위젯 목록이 각각 캐시돼 있다. 지운 기사가 다른 탭에 남아 있으면 안 지워진 것처럼 보인다.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.newsItems }),
  });
}

/** 한 회차의 결과. 에이전트가 준 숫자를 그대로 받는다. */
export type NewsCollectOutcome = {
  sections: number;
  fetched: number;
  created: number;
  skipped: number;
  failed: number;
  deleted: number;
};

/**
 * 지금 한 바퀴 수집한다.
 *
 * 수집 워커는 기본이 켜짐이고 1시간마다 돈다(에이전트 `AGENT_NEWS_WORKER`·`AGENT_NEWS_POLL_MINUTES`).
 * 그래도 이 버튼이 필요한 이유는 둘이다 — 워커를 끈 환경에서는 목록이 영영 비어 있고, 켠 환경에서도
 * 다음 회차까지 최대 한 시간이 남는다. 화면만 보는 사람은 "아직 기사가 없는 것" 과 "수집이 아예 안
 * 도는 것" 을 구분할 수 없으므로, 관리자가 직접 돌려 보고 결과 숫자를 확인할 수 있게 한다.
 *
 * 브라우저가 에이전트를 직접 부르지는 않는다 — 그 경로는 서버 간 공유 비밀로 막혀 있고,
 * 권한 판정은 backend 가 한다(`POST /v1/news-headlines/collect`).
 */
export function useCollectNews(): UseMutationResult<NewsCollectOutcome, Error, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiFetch<NewsCollectOutcome>(`${BASE}/collect`, { method: 'POST' }),
    // 새로 들어온 기사는 섹션 탭·홈 위젯 어디에나 걸린다. 목록을 통째로 다시 읽는다.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.newsItems }),
  });
}

/**
 * "3시간 전" 처럼 읽는다. 아침에 훑는 화면이라 <b>얼마나 지난 기사인가</b>가 날짜보다 중요하다.
 *
 * 하루가 넘으면 날짜로 바꾼다 — "37시간 전" 은 아무도 머릿속에서 환산하지 않는다.
 */
export function relativeTime(iso: string | null): string {
  if (!iso) return '';

  const at = new Date(iso);
  const minutes = Math.floor((Date.now() - at.getTime()) / 60_000);

  if (minutes < 1) return '방금';
  if (minutes < 60) return `${minutes}분 전`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)}시간 전`;
  return at.toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' });
}
